package one.healthium.onboarding.ui.components

import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.text.InlineTextContent
import androidx.compose.material3.LocalTextStyle
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.Placeholder
import androidx.compose.ui.text.PlaceholderVerticalAlign
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.em
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format.MonthNames
import kotlinx.datetime.format.char
import kotlinx.datetime.toLocalDateTime
import one.healthium.domain.user.models.PersonalPlanPeriod
import one.healthium.domain.user.models.prognoseDays
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.green_7
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant

@Composable
internal fun AnimatableAppText(
    modifier: Modifier = Modifier,
    text: String?,
    userState: UserProfileUiModel,
    style: TextStyle = LocalTextStyle.current,
    color: Color = Color.Unspecified,
    textAlign: TextAlign? = null,
) {
    val in3months = userState.model.prognoseDays(PersonalPlanPeriod.In3month)
    val in3monthsShort = userState.model.prognoseDays(PersonalPlanPeriod.In3monthShort)
    val startDate = Clock.System.now().plus(in3months.days)
    val endDate = Clock.System.now().plus(in3monthsShort.days)
    val datesList = remember(startDate, endDate) {
        getDatesBetween(startDate, endDate).map { formatDate(it) }
    }

    MeasureUnconstrainedViewWidth(
        viewToMeasure = {
            AnimatableItem(
                value = datesList.maxByOrNull { it.length } ?: "",
                style = style,
            )
        }
    ) { width, height ->
        AppText(
            modifier = modifier,
            text = text?.bindPlaceholderValues(userState),
            style = style,
            color = color,
            textAlign = textAlign,
            inlineContent = mapOf(
                "animatable_in_3months_short" to InlineTextContent(
                    placeholder = Placeholder(
                        width = dpToEm(width, style.fontSize),
                        height = 1.em,
                        placeholderVerticalAlign = PlaceholderVerticalAlign.TextCenter,
                    ),
                    children = {
                        AnimatableCalendarWheel(
                            modifier = Modifier.fillMaxSize(),
                            datesList = datesList,
                            height = height,
                            style = style.copy(color = green_7), // TODO remove color hardcode
                        )
                    }
                )
            )
        )
    }
}

private fun getDatesBetween(startDate: Instant, endDate: Instant): List<Instant> {
    val leftDate = if (startDate < endDate) startDate else endDate
    val rightDate = if (startDate < endDate) endDate else startDate
    val reverse = startDate > endDate

    val dates = mutableListOf<Instant>()
    var currentDate = leftDate
    while (currentDate <= rightDate) {
        if (reverse) {
            dates.add(0, currentDate)
        } else {
            dates.add(currentDate)
        }
        currentDate = currentDate.plus(1.days)
    }
    return dates
}

private val dateFormat = LocalDate.Format {
    monthName(MonthNames.ENGLISH_FULL)
    char(' ')
    dayOfMonth()
}

private fun formatDate(endDate: Instant) = dateFormat.format(endDate.toLocalDateTime(TimeZone.currentSystemDefault()).date)

@Composable
fun dpToEm(dp: Dp, fontSize: TextUnit): TextUnit {
    val density = LocalDensity.current
    val dpInPx = with(density) { dp.toPx() }
    val spInPx = with(density) { fontSize.toPx() }
    return (dpInPx / spInPx).em
}
